import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export type ServicioAsignable = {
  id_servicio: string;
  servicio: string;
  activo: boolean;
};

export function useServiciosAsignablesTrabajador(
  idEmpresa: string | null,
  idUsuario: string | null
) {
  const [servicios, setServicios] = useState<ServicioAsignable[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ============================================================
  // FETCH SERVICIOS
  // ============================================================
  const fetchServicios = useCallback(async () => {
    if (!idEmpresa) return;
    setLoading(true);

    try {
      const { data: serviciosEmpresa, error: errorEmpresa } = await supabase
        .from('vista_servicios_detalle')
        .select('id_servicio, servicio')
        .eq('id_empresa', idEmpresa)
        .eq('activo', true);

      if (errorEmpresa) throw errorEmpresa;

      let asignados: any[] = [];
      if (idUsuario) {
        const { data, error } = await supabase
          .from('servicios_usuarios')
          .select('id_servicio, activo')
          .eq('id_usuario', idUsuario);

        if (error) throw error;
        asignados = data || [];
      }

      const mapAsignados = new Map(
        asignados.map((s) => [String(s.id_servicio), s.activo])
      );

      const fusion = (serviciosEmpresa || []).map((s) => ({
        id_servicio: s.id_servicio,
        servicio: s.servicio,
        activo: mapAsignados.get(String(s.id_servicio)) ?? false,
      }));

      setServicios(fusion);
    } catch (e) {
      Sentry.captureException(e, { extra: { idEmpresa, idUsuario } });
      toast.error('Error cargando servicios del trabajador');
    } finally {
      setLoading(false);
    }
  }, [idEmpresa, idUsuario]);

  useEffect(() => {
    fetchServicios();
  }, [fetchServicios]);

  // ============================================================
  // TOGGLE (solo se usa en UI)
  // ============================================================
  const toggleServicio = useCallback(
    async (id_servicio: string, activo: boolean, onDone?: () => void) => {
      if (!idUsuario) {
        setServicios((prev) =>
          prev.map((s) =>
            s.id_servicio === id_servicio ? { ...s, activo } : s
          )
        );
        return;
      }

      setSaving(true);

      try {
        const { data: existente, error: errorCheck } = await supabase
          .from('servicios_usuarios')
          .select('id_servicio')
          .eq('id_usuario', idUsuario)
          .eq('id_servicio', id_servicio)
          .maybeSingle();

        if (errorCheck) throw errorCheck;

        if (existente) {
          const { error } = await supabase
            .from('servicios_usuarios')
            .update({ activo })
            .eq('id_usuario', idUsuario)
            .eq('id_servicio', id_servicio);

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('servicios_usuarios')
            .insert([{ id_usuario: idUsuario, id_servicio, activo }]);

          if (error) throw error;
        }

        setServicios((prev) =>
          prev.map((s) =>
            s.id_servicio === id_servicio ? { ...s, activo } : s
          )
        );

        onDone?.();
      } catch (e) {
        Sentry.captureException(e, {
          extra: { idUsuario, id_servicio, activo },
        });
        toast.error('Error actualizando servicio');
      } finally {
        setSaving(false);
      }
    },
    [idUsuario]
  );

  // ============================================================
  // CREACIÓN — guardar servicios
  // ============================================================
  const guardarServiciosNuevoTrabajador = useCallback(
    async (nuevoUserId: string) => {
      try {
        const activos = servicios.filter((s) => s.activo);

        if (activos.length === 0) return { ok: true };

        const payload = activos.map((s) => ({
          id_usuario: nuevoUserId,
          id_servicio: s.id_servicio,
          activo: true,
        }));

        const { error } = await supabase
          .from('servicios_usuarios')
          .insert(payload);

        if (error) throw error;

        return { ok: true };
      } catch (e) {
        Sentry.captureException(e, { extra: { nuevoUserId } });
        toast.error('No se pudieron guardar los servicios asignados');
        return { ok: false, error: e };
      }
    },
    [servicios]
  );

  // ============================================================
  // EDICIÓN — guardar servicios de verdad (USANDO LA LISTA DEL UI)
  // ============================================================
  const guardarServiciosTrabajador = useCallback(
    async (idUsuarioReal: string, listaServicios: ServicioAsignable[]) => {
      try {
        const { error: errorDelete } = await supabase
          .from('servicios_usuarios')
          .delete()
          .eq('id_usuario', idUsuarioReal);

        if (errorDelete) throw errorDelete;

        const payload = listaServicios.map((s) => ({
          id_usuario: idUsuarioReal,
          id_servicio: s.id_servicio,
          activo: s.activo,
        }));

        if (payload.length > 0) {
          const { error } = await supabase
            .from('servicios_usuarios')
            .insert(payload);

          if (error) throw error;
        }

        return { ok: true };
      } catch (e) {
        Sentry.captureException(e, { extra: { idUsuarioReal } });
        return { ok: false, error: 'Error guardando servicios' };
      }
    },
    []
  );

  // ============================================================
  // Permitir establecer los servicios desde el UI
  // ============================================================
  const setServiciosExternos = useCallback((lista: ServicioAsignable[]) => {
    setServicios(lista);
  }, []);

  // ============================================================
  // EXPORT
  // ============================================================
  return {
    servicios,
    loading,
    saving,
    toggleServicio,
    refetch: fetchServicios,
    guardarServiciosNuevoTrabajador,
    guardarServiciosTrabajador,
    setServiciosExternos,
  };
}
